import { useMemo } from 'react';

import { useGetOrganizationContactsQuery } from '@/entities/contacts';
import { useGetOrganizationQuery, useGetOrganizationsQuery } from '@/entities/organizations';
import type { ServiceRequestDetail } from '@/entities/service-request';
import { useProjectSiteParams } from '@/shared/lib';

import { transformSrfDetailToForm } from '../lib/consignment-transformers';
import {
  findContact,
  findOrganizationByName,
  SRF_PARTY_FIELDS,
  type SrfPartyRole,
  type SrfResolvedParty
} from '../lib/srf-parties';
import type { ConsignmentFormValues } from './validation';

type FieldTexts = Partial<Record<keyof ConsignmentFormValues, string>>;

interface SrfPartyRequest {
  orgId?: number | null;
  orgName?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
}

function useSrfParty({ orgId, orgName, contactName, contactEmail }: SrfPartyRequest) {
  const { projectId, siteId } = useProjectSiteParams();
  const hasContext = projectId != null && siteId != null;

  const byId = useGetOrganizationQuery({ id: orgId ?? 0, projectId, siteId }, { skip: orgId == null || !hasContext });

  const searchName = orgName?.trim();
  const shouldSearchByName = Boolean(searchName) && (orgId == null || byId.isError);
  const byName = useGetOrganizationsQuery({ search: searchName }, { skip: !shouldSearchByName });

  const organization =
    byId.currentData ??
    (shouldSearchByName ? findOrganizationByName(byName.currentData?.organizations, searchName) : undefined);

  const contactQuery = contactName?.trim();
  const contacts = useGetOrganizationContactsQuery(
    { orgId: String(organization?.id), projectId: projectId ?? 0, siteId: siteId ?? 0, q: contactQuery },
    { skip: !organization || !contactQuery || !hasContext }
  );
  const contact = organization ? findContact(contacts.currentData, contactName, contactEmail) : undefined;

  const party = useMemo<SrfResolvedParty>(
    () => ({
      organization: organization && { id: String(organization.id), name: organization.name },
      contact: contact?.id ? { id: String(contact.id), name: contact.name } : undefined
    }),
    [organization, contact]
  );

  return {
    party,
    isResolving: byId.isFetching || byName.isFetching || contacts.isFetching
  };
}

/**
 * Prefill of the create form from a service request being accepted, resolved the way the backend's
 * `moveToConsignment` resolves it: an organization by its id, else — for sender and receiver only —
 * by its whole name; a contact of that organization by name and first email. Found organizations and
 * contacts also come back as labels, because the organization list the form loads stops at the first
 * hundred and never holds clearing agents. What the requester typed but nothing matched seeds the
 * field's search instead. Nothing is returned until every lookup has settled: the form applies a
 * prefill once.
 */
export function useSrfPrefill(srf?: ServiceRequestDetail) {
  const sender = useSrfParty({
    orgId: srf?.senderOrgId,
    orgName: srf?.senderOrgName,
    contactName: srf?.senderContactName,
    contactEmail: srf?.senderContactEmail
  });
  const receiver = useSrfParty({
    orgId: srf?.receiverOrgId,
    orgName: srf?.receiverOrgName,
    contactName: srf?.receiverContactName,
    contactEmail: srf?.receiverContactEmail
  });
  const clearingAgent = useSrfParty({
    orgId: srf?.clearingOrgId,
    contactName: srf?.clearingContactName,
    contactEmail: srf?.clearingContactEmail
  });

  const isResolving = sender.isResolving || receiver.isResolving || clearingAgent.isResolving;

  return useMemo(() => {
    if (!srf || isResolving) return { isResolving };

    const parties: Record<SrfPartyRole, SrfResolvedParty> = {
      sender: sender.party,
      receiver: receiver.party,
      clearingAgent: clearingAgent.party
    };
    const typed: Record<SrfPartyRole, { organization?: string | null; contact?: string | null }> = {
      sender: { organization: srf.senderOrgName, contact: srf.senderContactName },
      receiver: { organization: srf.receiverOrgName, contact: srf.receiverContactName },
      clearingAgent: { contact: srf.clearingContactName }
    };

    const labels: FieldTexts = {};
    const hints: FieldTexts = {};
    for (const role of Object.keys(SRF_PARTY_FIELDS) as SrfPartyRole[]) {
      const fields = SRF_PARTY_FIELDS[role];
      const { organization, contact } = parties[role];
      if (organization) {
        labels[fields.organization] = organization.name;
      } else if (typed[role].organization?.trim()) {
        hints[fields.organization] = typed[role].organization.trim();
      }
      if (contact) {
        labels[fields.contact] = contact.name;
      } else if (organization && typed[role].contact?.trim()) {
        hints[fields.contact] = typed[role].contact.trim();
      }
    }

    return {
      prefillValues: transformSrfDetailToForm(srf, parties),
      prefillLabels: labels,
      prefillHints: hints,
      isResolving
    };
  }, [srf, isResolving, sender.party, receiver.party, clearingAgent.party]);
}
